import type { AnalyzerSummary, CanFrame, CanIdSummary } from './types';

const MIN_RELIABLE_DURATION_MS = 1000;
const TYPICAL_CAN_FRAME_RATE_WARNING_HZ = 500;

export function buildAnalyzerSummary(frames: CanFrame[], fileSize: number): AnalyzerSummary {
  let first: number | null = null;
  let last: number | null = null;
  for (const frame of frames) {
    if (!Number.isFinite(frame.timestampMs)) continue;
    first = first === null ? frame.timestampMs : Math.min(first, frame.timestampMs);
    last = last === null ? frame.timestampMs : Math.max(last, frame.timestampMs);
  }
  const durationMs = first !== null && last !== null ? Math.max(0, last - first) : 0;
  const uniqueCanIds = new Set(frames.map((frame) => frame.canId)).size;
  let timestampsMonotonic = true;
  let previous = Number.NEGATIVE_INFINITY;
  for (const frame of frames) {
    if (!Number.isFinite(frame.timestampMs)) continue;
    if (frame.timestampMs < previous) {
      timestampsMonotonic = false;
      break;
    }
    previous = frame.timestampMs;
  }
  const averageFps = durationMs > 0 ? frames.length / (durationMs / 1000) : 0;
  const durationReliable = durationMs >= MIN_RELIABLE_DURATION_MS;
  const fpsReliable = timestampsMonotonic
    && durationReliable
    && averageFps > 0
    && averageFps <= TYPICAL_CAN_FRAME_RATE_WARNING_HZ;
  const timestampWarning = !timestampsMonotonic || !durationReliable || averageFps > TYPICAL_CAN_FRAME_RATE_WARNING_HZ
    ? 'Capture timestamps appear non-monotonic or incomplete; FPS estimate may be invalid.'
      : undefined;

  return {
    totalFrames: frames.length,
    uniqueCanIds,
    firstTimestampMs: first,
    lastTimestampMs: last,
    durationMs,
    averageFps: fpsReliable ? averageFps : null,
    fileSize,
    timestampWarning,
    timestampsMonotonic,
    fpsReliable
  };
}

export function summarizeCanIds(frames: CanFrame[]): CanIdSummary[] {
  const summaries = new Map<number, CanIdSummary & { lastDataHex?: string }>();

  for (const frame of frames) {
    const existing = summaries.get(frame.canId);
    if (!existing) {
      summaries.set(frame.canId, {
        canId: frame.canId,
        canIdHex: frame.canIdHex,
        frameCount: 1,
        frequencyHz: 0,
        dlcValues: [frame.dlc],
        firstSeenMs: frame.timestampMs,
        lastSeenMs: frame.timestampMs,
        changedCount: 0,
        lastDataHex: frame.dataHex
      });
      continue;
    }

    existing.frameCount += 1;
    existing.firstSeenMs = Math.min(existing.firstSeenMs, frame.timestampMs);
    existing.lastSeenMs = Math.max(existing.lastSeenMs, frame.timestampMs);
    if (!existing.dlcValues.includes(frame.dlc)) existing.dlcValues.push(frame.dlc);
    if (existing.lastDataHex !== frame.dataHex) existing.changedCount += 1;
    existing.lastDataHex = frame.dataHex;
  }

  return Array.from(summaries.values())
    .map((entry) => {
      const summary = { ...entry };
      delete summary.lastDataHex;
      const durationSeconds = Math.max(0, (summary.lastSeenMs - summary.firstSeenMs) / 1000);
      return {
        ...summary,
        dlcValues: summary.dlcValues.sort((a, b) => a - b),
        frequencyHz: durationSeconds > 0 ? summary.frameCount / durationSeconds : summary.frameCount
      };
    })
    .sort((a, b) => b.frameCount - a.frameCount);
}
